import type { UUID } from "@/Shared/types";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { CustomDocsUpdateSelectModeStage } from "@/Shared/CustomDocs/CustomComponents/CustomDocsUpdateSelectModeStage";
import { CustomDocsUpdateSubmitStage } from "@/Shared/CustomDocs/CustomComponents/CustomDocsUpdateSubmitStage";
import { CustomsDocsUploadMode } from "@/Apis/CustomsDocsUploadMode";
import { CustomDocsUpdateStageType } from "@/Shared/CustomDocs/Types/CustomDocsUpdateStageType";

interface UpdateCustomsDocsDialogProps {
  shipmentId: UUID;
  onDone: () => void;
}

export interface UpdateCustomsDocsStageProps extends UpdateCustomsDocsDialogProps {
  stage: CustomDocsUpdateStageType;
  setStage: Dispatch<SetStateAction<CustomDocsUpdateStageType>>;
  mode: CustomsDocsUploadMode | undefined;
  setMode: Dispatch<SetStateAction<CustomsDocsUploadMode | undefined>>;
}

interface CustomDocsUpdateDialogProps extends DialogBaseProps {
  shipmentId: UUID;
  /** Fired when the flow finishes so the caller can reload documents. */
  onDone: () => void;
}

// Two-stage customs-docs update flow (pick upload mode, then submit) inside
// one dialog. Replaces the legacy imperative modal; the stages own their own
// buttons and call onDone when the flow completes.
export function CustomDocsUpdateDialog(props: CustomDocsUpdateDialogProps) {
  const [mode, setMode] = useState<CustomsDocsUploadMode>();
  const [stage, setStage] = useState<CustomDocsUpdateStageType>(
    CustomDocsUpdateStageType.SELECT_MODE,
  );

  function onDone() {
    props.onOpenChange?.(false);
    props.onDone();
  }

  const stageProps: UpdateCustomsDocsStageProps = {
    mode,
    setMode,
    setStage,
    stage,
    shipmentId: props.shipmentId,
    onDone,
  };

  return (
    // The title is the dialog's, not the stage's: both stages are the same
    // task, so the header stays put while the body swaps.
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      width="medium"
      title="Update Customs Documents"
    >
      <ControlPresentationProvider presentation="underlined">
        {stage === CustomDocsUpdateStageType.SELECT_MODE && (
          <CustomDocsUpdateSelectModeStage {...stageProps} />
        )}
        {stage === CustomDocsUpdateStageType.SUBMIT && (
          <CustomDocsUpdateSubmitStage {...stageProps} />
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}
